import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { updateLocation } from '../store/slices/authSlice';
import { usersAPI } from '../services/api';
import logo from '../assets/logo.png';
import './Auth.css';

const AdminLocationSetup: React.FC = () => {
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useGPS, setUseGPS] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const handleGPS = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by this browser');
      return;
    }
    setUseGPS(true);
    setLoading(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const result = await dispatch(updateLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          })).unwrap();
          // updateLocation returns the updated user fields — persist to localStorage
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          Object.assign(stored, result);
          localStorage.setItem('user', JSON.stringify(stored));
          window.location.href = '/';
        } catch (err: any) {
          setError(err || 'Failed to update location');
          setLoading(false);
        }
      },
      (err) => {
        setError('GPS error: ' + err.message);
        setLoading(false);
        setUseGPS(false);
      }
    );
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!city.trim() || !stateProvince.trim() || !country.trim()) {
      setError('All fields are required');
      return;
    }
    if (!user) return;

    setLoading(true);
    setError('');
    try {
      await usersAPI.updateUser(user.id, {
        city: city.trim(),
        state_province: stateProvince.trim(),
        country: country.trim(),
      });

      // Update localStorage so the redirect check sees the new city
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.city = city.trim();
      stored.state_province = stateProvince.trim();
      stored.country = country.trim();
      localStorage.setItem('user', JSON.stringify(stored));

      // Force page reload to re-read localStorage into Redux state
      window.location.href = '/';
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save location');
    }
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <img src={logo} alt="Meerkapp" className="auth-logo-img" />
          <h1>Set Your Location</h1>
          <p>Choose how to set your location for the feed</p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
            <button type="button" onClick={() => setError('')}>×</button>
          </div>
        )}

        <div style={{ marginBottom: 24 }}>
          <button
            className="btn-primary auth-btn"
            onClick={handleGPS}
            disabled={loading}
            style={{ marginBottom: 16 }}
          >
            {useGPS && loading ? 'Detecting location...' : 'Use GPS Location'}
          </button>

          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', margin: '16px 0' }}>
            — or set manually —
          </div>
        </div>

        <form onSubmit={handleManualSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Edmonton"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="stateProvince">State / Province</label>
            <input
              type="text"
              id="stateProvince"
              value={stateProvince}
              onChange={(e) => setStateProvince(e.target.value)}
              placeholder="e.g. Alberta"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. Canada"
              maxLength={100}
            />
          </div>

          <button type="submit" className="btn-primary auth-btn" disabled={loading}>
            {loading ? 'Saving...' : 'Save Location'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLocationSetup;
